// Iter-46an: super-admin payment health dashboard.
// Surfaces: RyftPay mode, card totals, stale-card flags, studios pending
// onboarding, and a button to run the on-demand stale-customer batch scan.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Building2,
  Users as UsersIcon,
  RefreshCw,
  Loader2,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { apiGet, apiPost } from '../api';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

const Stat = ({ icon: Icon, label, value, tone = 'default', testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-5">
      <div className="flex items-center gap-3">
        <div
          className={`p-2 rounded-md ${
            tone === 'warn'
              ? 'bg-amber-100 text-amber-700'
              : tone === 'danger'
              ? 'bg-red-100 text-red-700'
              : tone === 'good'
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold">{value}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AdminPaymentHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet('/api/admin/payments/health');
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load payment health'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runScan = async () => {
    if (!window.confirm('Run a stale-customer scan against RyftPay? Walks every user with a Ryft customer id.')) return;
    setScanning(true);
    try {
      const res = await apiPost('/api/admin/payments/scan-stale-customers');
      toast.success(res.message || 'Scan complete');
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Scan failed'));
    } finally {
      setScanning(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading payment health…
      </div>
    );
  }

  if (!data) return null;

  const { mode, configured, cards, customers, studios, studios_pending_onboarding, recent_stale_cards } = data;
  const onboardingCoverage = studios.total > 0
    ? Math.round((studios.can_accept_payments / studios.total) * 100)
    : 0;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="payment-health-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Payment Health
          </h1>
          <p className="text-sm text-muted-foreground">
            Snapshot of platform-wide payment plumbing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={mode === 'live' ? 'default' : 'secondary'}
            data-testid="payment-mode-badge"
          >
            {configured ? `RyftPay: ${mode.toUpperCase()}` : 'RyftPay: NOT CONFIGURED'}
          </Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="refresh-health-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={runScan}
            disabled={scanning}
            data-testid="run-stale-scan-btn"
          >
            {scanning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
            Run stale-customer scan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={CreditCard} label="Total saved cards" value={cards.total} testid="stat-cards-total" />
        <Stat
          icon={AlertTriangle}
          label="Cards needing re-entry"
          value={cards.needs_reentry}
          tone={cards.needs_reentry > 0 ? 'warn' : 'good'}
          testid="stat-cards-stale"
        />
        <Stat icon={UsersIcon} label="Customers with Ryft id" value={customers.with_ryft_id} testid="stat-customers" />
        <Stat
          icon={Building2}
          label={`Studios accepting payments (${onboardingCoverage}%)`}
          value={`${studios.can_accept_payments}/${studios.total}`}
          tone={onboardingCoverage >= 80 ? 'good' : onboardingCoverage >= 50 ? 'warn' : 'danger'}
          testid="stat-studios"
        />
      </div>

      {/* Studios pending onboarding */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Studios with incomplete onboarding ({studios.pending_onboarding_count})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {studios_pending_onboarding.length === 0 ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              All onboarded studios have completed KYC.
            </div>
          ) : (
            <div className="space-y-2" data-testid="pending-studios-list">
              {studios_pending_onboarding.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between border rounded-md p-2"
                  data-testid={`pending-studio-${s.id}`}
                >
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {s.ryftpay_account_id}
                    </div>
                  </div>
                  {s.slug && (
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/studio/${s.slug}`} target="_blank" rel="noreferrer">View</Link>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent stale cards */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Recent stale-card flags
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recent_stale_cards.length === 0 ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              No stale cards detected.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recent-stale-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                    <th className="py-2 pr-2">User</th>
                    <th className="py-2 pr-2">Card</th>
                    <th className="py-2 pr-2">Reason</th>
                    <th className="py-2 pr-2">Flagged at</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_stale_cards.map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="py-2 pr-2">{c.user_email || c.user_id}</td>
                      <td className="py-2 pr-2">
                        {c.card_brand ? `${c.card_brand[0].toUpperCase() + c.card_brand.slice(1)} ` : ''}
                        •••• {c.card_last_four || '----'}
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground">
                        {c.needs_reentry_reason || 'unknown'}
                      </td>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">
                        {c.needs_reentry_at
                          ? new Date(c.needs_reentry_at).toLocaleString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
